import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react-lite'
import DashboardLayout from '../../../components/Admin/DashboardLayout'
import { MDBCol, MDBContainer, MDBRow } from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import { endOfWeek, format, startOfWeek, subWeeks } from 'date-fns'
import AdminApi from '../../../api/admin-api/admin-api'
import { WheelOfLoveWinnerFactory, LedgerFactory } from '../../../shared/factories'
import WheelOfLoveWinnerService from '../../../shared/services/WheelOfLoveWinner.service'
import LedgerTransactionService from '../../../shared/services/LedgerTransaction.service'
import EliteAgencyTeamService from '../../../shared/services/EliteAgencyTeam.service'
import { getThresholdForScope } from '../../../shared/utilities/breakTheBarThreshold'
import ContestStore, { WINNER_AMOUNT, NON_WINNER_AMOUNT } from './Contest.store'
import WeekSelector from './components/WeekSelector.component'
import WheelSpinner from './components/WheelSpinner.component'
import Contestants from './components/Contestants.component'
import ContestController from './components/ContestController.component'
import WinnerModal from './components/WinnerModal.component'

import './WheelOfLove.scss'

// Scopes pulled onto the wheel:
//   - elite_agency_team: a team hitting the team threshold (e.g. 2,400) goes
//                        on the wheel under the team's name.
//   - sig_agent:        a sig agent hitting the individual threshold (e.g.
//                       1,000) goes on under their own name.
//   - career_agent:     a career agent hitting the career threshold (e.g.
//                       1,000) goes on under their own name.
// Thresholds come from 'usertypes---break-the-bar-thresholds'.
// Order matters: elite_agency_team is listed first so when a team owner also
// broke the bar individually, the team entry wins the dedup (team name shows).
// District / Region / Division aggregates are intentionally NOT pulled.
const BAR_BREAKER_SCOPES = [
  { key: 'elite_agency_id', scope: 'elite_agency_team' },
  { key: 'user_id', scope: 'sig_agent' },
  { key: 'sub_agent_id', scope: 'career_agent' },
]

// Observer wrapper so the class component re-renders on MobX changes
const WheelOfLoveLayout = observer(({ loading, dateFrom, dateTo, onWeekChange, onEndContest, onSpinComplete }) => {
  const { isPlaying, hideNamesList } = ContestStore
  const showWeekSection = !isPlaying
  const hideList = isPlaying && hideNamesList

  return (
    <MDBContainer fluid id='WheelOfLove'>
      {showWeekSection && (
        <>
          <h3 className='text-center'>Wheel of Love</h3>
          <hr />
        </>
      )}

      {loading ? (
        <h2 className='text-center'>Loading...</h2>
      ) : (
        <>
          {showWeekSection && (
            <>
              <WeekSelector
                dateFrom={dateFrom}
                dateTo={dateTo}
                onWeekChange={onWeekChange}
                disabled={!showWeekSection}
              />
              <div className='mb-4' />
            </>
          )}

          <ContestController onEndContest={onEndContest} />

          <MDBRow>
            <MDBCol size='12' lg={hideList ? '12' : '8'} className={hideList ? 'd-flex justify-content-center' : ''}>
              <WheelSpinner onSpinComplete={onSpinComplete} />
            </MDBCol>
            {!hideList && (
              <MDBCol size='12' lg='4'>
                <Contestants />
              </MDBCol>
            )}
          </MDBRow>
        </>
      )}

      <WinnerModal />
    </MDBContainer>
  )
})

class WheelOfLove extends Component {
  constructor(props) {
    super(props)

    const now = new Date()
    this.state = {
      loading: false,
      dateFrom: startOfWeek(now, { weekStartsOn: 1 }),
      dateTo: endOfWeek(now, { weekStartsOn: 1 }),
    }
  }

  async componentDidMount() {
    document.addEventListener('fullscreenchange', this._onFullscreenChange)

    const { prevWeekStart, prevWeekEnd } = this._getWeekDates()
    ContestStore.setWeekDates(prevWeekStart, prevWeekEnd)

    // Try to restore saved contest state from DB for this week
    const restored = await ContestStore.loadState(prevWeekStart)
    if (restored) {
      return
    }

    ContestStore.resetContest()
    ContestStore.setWeekDates(prevWeekStart, prevWeekEnd)
    this._loadWeek()
  }

  componentWillUnmount() {
    document.removeEventListener('fullscreenchange', this._onFullscreenChange)
  }

  _onFullscreenChange = () => {
    if (!document.fullscreenElement && ContestStore.fullscreen) {
      ContestStore.fullscreen = false
    }
  }

  _onWeekChange = (dateFrom, dateTo) => {
    this.setState({ dateFrom, dateTo }, async () => {
      const { prevWeekStart, prevWeekEnd } = this._getWeekDates()
      ContestStore.resetContest()
      ContestStore.setWeekDates(prevWeekStart, prevWeekEnd)

      const restored = await ContestStore.loadState(prevWeekStart)
      if (!restored) {
        this._loadWeek()
      }
    })
  }

  _loadWeek = async () => {
    await this._fetchBarBreakers()
    await this._fetchExistingWinners()
  }

  _getWeekDates = () => {
    const prevWeekStart = format(startOfWeek(subWeeks(this.state.dateFrom, 1), { weekStartsOn: 1 }), 'yyyy-MM-dd')
    const prevWeekEnd = format(endOfWeek(subWeeks(this.state.dateFrom, 1), { weekStartsOn: 1 }), 'yyyy-MM-dd')
    return { prevWeekStart, prevWeekEnd }
  }

  _fetchExistingWinners = async () => {
    try {
      const { prevWeekStart, prevWeekEnd } = this._getWeekDates()
      const results = await WheelOfLoveWinnerFactory.search({
        search: {
          contest_week_start: prevWeekStart,
          contest_week_end: prevWeekEnd,
        },
        pagination: false,
      })

      // Handle various response shapes
      let records = []
      if (Array.isArray(results)) records = results
      else if (results && results.models) records = results.models
      else if (results && results.data && Array.isArray(results.data)) records = results.data

      if (records.length > 0) {
        const getF = (r, f) => (typeof r.get === 'function' ? r.get(f) : r[f])

        // Build contestants list from saved records only
        const savedContestants = records.map(w => ({
          user_id: parseInt(getF(w, 'user_id')),
          u_name: getF(w, 'agent_name'),
          visible: true,
          manuallyAdded: false,
          winnings: parseFloat(getF(w, 'amount')),
        }))

        // Replace the contestants list with only the saved participants
        ContestStore.contestants = savedContestants

        // Build winners list (only $100 winners)
        ContestStore.winners = []
        records.forEach(w => {
          const amount = parseFloat(getF(w, 'amount'))
          if (amount === WINNER_AMOUNT) {
            ContestStore.winners.push({
              user_id: parseInt(getF(w, 'user_id')),
              name: getF(w, 'agent_name'),
              amount: amount,
              spinIndex: parseInt(getF(w, 'spin_index')),
            })
          }
        })

        ContestStore.setMode('completed')
      }
    } catch (err) {
      console.error('Error fetching existing winners:', err)
    }
  }

  _fetchBarBreakers = async () => {
    this.setState({ loading: true })

    try {
      const { prevWeekStart, prevWeekEnd } = this._getWeekDates()

      // Build an owner_id → team_name lookup so agents who lead an active
      // elite agency team display the team's name on the wheel instead of
      // their own. Calls the service directly so we can handle whichever
      // response shape comes back instead of going through the factory's
      // Model wrapper (which has previously returned an empty array here).
      // Only active (non-dissolved) teams contribute names.
      const teamNameByOwner = new Map()
      try {
        const response = await EliteAgencyTeamService.search({
          pagination: false,
        })
        let rawTeams = []
        if (Array.isArray(response)) rawTeams = response
        else if (Array.isArray(response?.models)) rawTeams = response.models
        else if (Array.isArray(response?.data)) rawTeams = response.data
        else if (Array.isArray(response?.payload)) rawTeams = response.payload

        rawTeams
          .filter(t => !t?.team_dissolved_at)
          .forEach(t => {
            const ownerId = t?.owner_id
            const teamName = t?.team_name
            if (ownerId && teamName) teamNameByOwner.set(parseInt(ownerId), teamName)
          })
        console.log(`[WOL] Loaded ${teamNameByOwner.size} active elite agency teams for name override`)
      } catch (e) {
        console.error('[WOL] Failed to fetch elite agency teams:', e)
      }

      // Fetch the sig_agent and elite_agency_team weekly-points leaderboards
      // in parallel. Each has its own threshold (typically 1,000 / 2,400) from
      // 'usertypes---break-the-bar-thresholds'.
      const scopeRows = await Promise.all(
        BAR_BREAKER_SCOPES.map(async ({ key, scope }) => {
          try {
            const result = await AdminApi.getLeaderboardData(
              'points',
              prevWeekStart,
              prevWeekEnd,
              null,
              key,
              'weekly',
              {}
            )
            return { scope, rows: result?.data?.payload?.rows || [] }
          } catch (err) {
            console.error(`[WOL] Failed to load ${scope} bar-breakers:`, err)
            return { scope, rows: [] }
          }
        })
      )

      // Filter each scope's rows by its threshold and map to contestant shape.
      // Display name comes from the scope itself: team scope shows the team
      // name, sig_agent scope shows the agent's own name. row.team_name is
      // never read directly — on sig_agent rows the backend fills that field
      // with the upline person's name, which was previously surfacing as the
      // wrong display label.
      const scopeResults = scopeRows.map(({ scope, rows }) => {
        const threshold = getThresholdForScope(scope, prevWeekStart)
        if (!threshold) return []
        const isTeamScope = scope === 'elite_agency_team'
        const isCareerAgent = scope === 'career_agent'

        return rows
          .filter(row => {
            const passesBase =
              row.u_name !== 'Totals:' &&
              !isNaN(parseFloat(row.total)) &&
              parseFloat(row.total) >= threshold &&
              row.user_id
            if (!passesBase) return false
            // Career agents are only listed on the wheel if they personally own
            // an active elite agency team. Career agents who don't own a team
            // are dropped — only sig agents and elite team owners go on the wheel.
            if (isCareerAgent) {
              return teamNameByOwner.has(parseInt(row.user_id))
            }
            return true
          })
          .map(row => {
            const userId = parseInt(row.user_id)
            const displayName = isTeamScope ? teamNameByOwner.get(userId) || row.team_name || row.u_name : row.u_name
            return {
              user_id: userId,
              u_name: displayName,
              u_fname: row.u_fname,
              u_lname: row.u_lname,
              usertype_id: row.usertype_id,
              scope,
            }
          })
      })

      // Dedup across scopes — e.g. a sig agent who also owns an elite team
      // that broke the bar should appear once. elite_agency_team is listed
      // first in BAR_BREAKER_SCOPES, so the team entry wins for shared user_ids.
      const seen = new Set()
      const barBreakers = []
      scopeResults.flat().forEach(b => {
        if (seen.has(b.user_id)) return
        seen.add(b.user_id)
        barBreakers.push(b)
      })

      if (barBreakers.length > 0) {
        ContestStore.setContestants(barBreakers)
        toast.success(`Loaded ${barBreakers.length} bar-breakers for ${prevWeekStart} - ${prevWeekEnd}`, {
          autoClose: 3000,
        })
      } else {
        toast.info('No bar-breakers found for this week.', { autoClose: 3000 })
      }
    } catch (err) {
      console.error('Error fetching bar breakers:', err)
      toast.error('Failed to load bar-breaker data.', { autoClose: 3000 })
    }

    this.setState({ loading: false })
  }

  _handleSpinComplete = winnerUserId => {
    // Winner modal handles the display now
  }

  _isAlreadyFinalized = async (weekStart, weekEnd) => {
    try {
      const results = await WheelOfLoveWinnerFactory.search({
        search: { contest_week_start: weekStart, contest_week_end: weekEnd },
        pagination: false,
      })
      let records = []
      if (Array.isArray(results)) records = results
      else if (results && results.models) records = results.models
      else if (results && results.data && Array.isArray(results.data)) records = results.data
      return records.length > 0
    } catch (err) {
      console.error('[WOL] _isAlreadyFinalized check failed:', err)
      return false
    }
  }

  _handleEndContest = async () => {
    const { prevWeekStart, prevWeekEnd } = this._getWeekDates()
    const today = format(new Date(), 'yyyy-MM-dd')
    const winnersOnly = ContestStore.winners

    // All visible contestants (includes winners removed from wheel since they're still visible)
    const toSave = ContestStore.contestants.filter(c => c.visible)

    const alreadyFinalized = await this._isAlreadyFinalized(prevWeekStart, prevWeekEnd)
    if (alreadyFinalized) {
      toast.error(
        'This contest was already finalized (it may have been ended by another admin). No new awards were made.',
        { autoClose: 8000 }
      )
      await this._fetchExistingWinners()
      return
    }

    let savedCount = 0
    let winnerCreditedCount = 0
    let nonWinnerCreditedCount = 0
    let skippedCount = 0

    for (const contestant of toSave) {
      const isWinner = contestant.winnings === WINNER_AMOUNT
      const amount = isWinner ? WINNER_AMOUNT : NON_WINNER_AMOUNT
      const agentName = contestant.u_name || `${contestant.u_fname || ''} ${contestant.u_lname || ''}`.trim()

      // Step 1: record the award row (idempotency anchor).
      try {
        await WheelOfLoveWinnerService.store({
          user_id: contestant.user_id,
          agent_name: agentName,
          amount,
          spin_index: isWinner ? winnersOnly.find(w => w.user_id === contestant.user_id)?.spinIndex || 0 : 0,
          contest_week_start: prevWeekStart,
          contest_week_end: prevWeekEnd,
        })
        savedCount++
      } catch (err) {
        skippedCount++
        console.warn(
          `[WOL] No award row inserted for ${agentName} (${contestant.user_id}); skipping ledger credit:`,
          err
        )
        continue
      }

      // Step 2: a fresh award row exists, so credit the ledger.
      try {
        const ledgers = await LedgerFactory.search({
          search: { user_id: contestant.user_id },
          pagination: false,
        })

        const ledger = Array.isArray(ledgers) && ledgers.length > 0 ? ledgers[0] : null
        if (ledger) {
          const ledgerId = typeof ledger.get === 'function' ? ledger.get('id') : ledger.id
          await LedgerTransactionService.store({
            ledger_id: ledgerId,
            type: 'AgentAchievement',
            category: 'WHEEL-OF-LOVE',
            credit: amount,
            debit: 0,
            purpose: isWinner
              ? `Wheel of Love Winner - Week ${prevWeekStart}`
              : `Wheel of Love Participant - Week ${prevWeekStart}`,
            trans_state: 'CLOSED',
            transact_at: today,
          })
          if (isWinner) winnerCreditedCount++
          else nonWinnerCreditedCount++
        } else {
          console.warn(`[WOL] No ledger found for user ${contestant.user_id} (${agentName})`)
        }
      } catch (err) {
        console.error(`[WOL] Failed to credit $${amount} to ${agentName}:`, err)
      }
    }

    if (savedCount > 0) {
      toast.success(`Saved contest results.`, { autoClose: 5000 })
    }

    if (winnerCreditedCount > 0) {
      toast.info(
        `Credited $${WINNER_AMOUNT} to ${winnerCreditedCount} winner${winnerCreditedCount !== 1 ? 's' : ''}.`,
        { autoClose: 5000 }
      )
    } else if (savedCount > 0 && winnersOnly.length > 0) {
      toast.warn('Results saved but ledger credits failed. Add USABG Bucks manually.', { autoClose: 8000 })
    }

    if (nonWinnerCreditedCount > 0) {
      toast.info(
        `Credited $${NON_WINNER_AMOUNT} to ${nonWinnerCreditedCount} non-winner${
          nonWinnerCreditedCount !== 1 ? 's' : ''
        }.`,
        { autoClose: 5000 }
      )
    }

    if (skippedCount > 0) {
      toast.warn(
        `${skippedCount} agent${
          skippedCount !== 1 ? 's were' : ' was'
        } already awarded and skipped (no duplicate credit).`,
        { autoClose: 8000 }
      )
    }
  }

  render() {
    toast.configure()

    const { loading, dateFrom, dateTo } = this.state

    return (
      <Fragment>
        <DashboardLayout>
          <main className='mainSection mt-5 pb-5'>
            <WheelOfLoveLayout
              loading={loading}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onWeekChange={this._onWeekChange}
              onEndContest={this._handleEndContest}
              onSpinComplete={this._handleSpinComplete}
            />
          </main>
        </DashboardLayout>
      </Fragment>
    )
  }
}

export default WheelOfLove
